/**
 * This component does not fully mimics what a `raster-dem` source in mapbox-gl can do,
 * if there is anything missing we can try adding and improving this component,
 * but also feel free to copy some of this code, customize it and add it to your
 * project for you specific use case.
 */

import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { useMap } from './useMap';
import { useMapStyle } from './useMapStyle';

// Decrease the exaggeration as we zoom in. This is similar to the expression used on mobile
// except the denominator is 9 there, which means web dampens the exaggeration further.
export const determineExaggerationFromZoom = (zoomLevel) => {
  return Math.exp((20 - zoomLevel) / 12);
};

function Terrain({ id, ...otherProps }) {
  const map = useMap();
  const mapStyle = useMapStyle();

  const addSource = () => {
    if (map.style?._loaded && !map.getSource(id)) {
      const data = {
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        // https://docs.mapbox.com/data/tilesets/reference/mapbox-terrain-dem-v1/ this layer does
        // not have any higher resolution than zoom 14
        maxzoom: 14,
        ...otherProps,
        type: 'raster-dem'
      };
      map.addSource(id, data);
      map.setTerrain({
        source: id,
        exaggeration: determineExaggerationFromZoom(map.getZoom())
      });
    }
  };

  /**
   * Render on map style changes
   */
  useEffect(() => {
    addSource();
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle]);

  /**
   * Component mount/unmount
   */
  useEffect(() => {
    // Change the exaggeration depending on the new zoom level
    function handleZoom() {
      map.setTerrain({
        source: id,
        exaggeration: determineExaggerationFromZoom(map.getZoom())
      });
    }
    map.on('zoom', handleZoom);

    return () => {
      if (map && map.style && map.getSource(id)) {
        map.off('zoom', handleZoom);
        map.setTerrain(null);
        map.removeSource(id);
      }
    };
    // TODO react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

/**
 * For more detail on available options look at the docs
 * @url https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/#raster-dem
 */

Terrain.defaultProps = {
  id: 'mapbox-dem'
};

Terrain.propTypes = {
  id: PropTypes.string
};

export default Terrain;
